import { memo, useRef, useEffect } from 'react';
import styled from '@emotion/styled';

import bp from '../lib/cssMediaQuery';
import focusImage from '../lib/focusImage';

import type { ThemeProps } from '@emotion/react';
import type { CSSObject } from '@emotion/styled';

interface PhotoPaneProps {
  renderingPreviewImage: boolean;
}

interface Props {
  xFocalPoint: number;
  yFocalPoint: number;
  coverPhotoUrl: string;
  renderingPreviewImage: boolean;
}

const PhotoPane = styled("div")`
  position: relative;
  min-width: 50%;
  flex-grow: 1;
  overflow: hidden;

  ${bp['max1280']} {
    min-width: 40%;
  }

  ${bp['max768']} {
    width: 100%;
    height: ${({ renderingPreviewImage }: PhotoPaneProps): string =>
      renderingPreviewImage ? '50vw !important' : '75vw'};
  }

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.PhotoPane}
`;

const PhotoCard = styled("div")`
  display: flex;
  ${({ theme }): CSSObject | undefined =>
    theme.liteSite?.CardLayout?.components?.PhotoCard}
`;
const CoupleImgWrapper = styled("div")`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
`;

const CoupleImgWithFocalPoint = styled('img')`
  object-fit: cover;
  max-width: none;
`;

function LiteSitePage({
  xFocalPoint,
  yFocalPoint,
  renderingPreviewImage,
  coverPhotoUrl,
}: Props) {
  const coverPhotoRef = useRef<HTMLImageElement | null>(null);

  focusImage(coverPhotoRef, xFocalPoint, yFocalPoint);

  useEffect((): void => {
    focusImage(coverPhotoRef, xFocalPoint, yFocalPoint);
  }, [coverPhotoRef, xFocalPoint, yFocalPoint]);

  // Flourishes are contained in h1/h2 for SEO purposes.
  return coverPhotoUrl !== '' ? (
    <PhotoPane renderingPreviewImage={renderingPreviewImage}>
      <PhotoCard className="photo-card">
        <CoupleImgWrapper>
          <CoupleImgWithFocalPoint
            ref={coverPhotoRef}
            src={coverPhotoUrl}
            data-focus-x={xFocalPoint}
            data-focus-y={yFocalPoint}
          />
        </CoupleImgWrapper>
      </PhotoCard>
    </PhotoPane>
  ) : null;
}

export default memo(LiteSitePage);
